import React, { ForwardedRef } from 'react';

import { Caption } from '../../../../../atoms/Caption/Caption';
import { StyledTextField, StyledTextFieldWrapper } from './TextField.styles';
import { StyledIconContainer, StyledInputContainer, StyledLabel } from './InputField.styles';

export type TextInputFieldType = 'text' | 'tel' | 'password';

export type InputAutoCompleteType =
  | 'username'
  | 'current-password'
  | 'on'
  | 'off'
  | 'one-time-code';

export type InputEnterKeyHintType =
  | 'search'
  | 'enter'
  | 'done'
  | 'go'
  | 'next'
  | 'previous'
  | 'send';

export type TextFieldIcon = {
  icon: React.ReactNode;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
};

export type TextFieldProps = {
  type: TextInputFieldType;
  name: string;
  disabled: boolean;
  errorCaption?: string;
  helpCaption?: string;
  hideCaption?: boolean;
  autoComplete: InputAutoCompleteType;
  flexRow?: boolean;
  onChange: (event: React.FormEvent<HTMLInputElement>) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  description?: string;
  value?: string;
  forwardRef?: ForwardedRef<HTMLInputElement>;
  placeholder?: string;
  index?: number;
  enterKeyHint?: InputEnterKeyHintType;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  startIcon?: TextFieldIcon;
  endIcon?: TextFieldIcon;
  alignIcon?: boolean;
  dataTestId?: string;
};

export const TextField = ({
  forwardRef,
  type,
  name,
  value,
  description,
  disabled,
  errorCaption,
  helpCaption,
  hideCaption = false,
  placeholder,
  autoComplete,
  enterKeyHint,
  onKeyDown,
  onChange,
  onFocus,
  onBlur,
  startIcon,
  endIcon,
  flexRow = false,
  index = 0,
  alignIcon = false,
  dataTestId = `input-field-${index}`,
}: TextFieldProps) => (
  <StyledInputContainer flex={flexRow && index > 0 ? 3 : 1} flexRow={flexRow}>
    <StyledTextFieldWrapper>
      {startIcon && (
        <StyledIconContainer onMouseDown={startIcon.onMouseDown} onClick={startIcon.onClick}>
          {startIcon.icon}
        </StyledIconContainer>
      )}
      <StyledTextField
        id={name}
        data-testid={dataTestId}
        name={name}
        ref={forwardRef}
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        error={Boolean(errorCaption)}
        floatingLabel={!!description} // only when a description exist
        disabled={disabled}
        aria-required
        {...(errorCaption || helpCaption
          ? {
              'aria-describedby': `describe-${name}`,
            }
          : {})}
        autoCorrect="off"
        autoCapitalize="none"
        enterKeyHint={enterKeyHint}
        onKeyDown={onKeyDown}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        alignIcon={alignIcon}
      />
      {description && <StyledLabel htmlFor={name}>{description}</StyledLabel>}
      {endIcon && (
        <StyledIconContainer
          // onMouseDown event is needed to not lose the :focus state on the input when clicking the icon
          onMouseDown={endIcon.onMouseDown}
          onClick={endIcon.onClick}
          cursor={endIcon.onClick ? 'pointer' : undefined} // assuming when onClick function exist, to show the pointer
        >
          {endIcon.icon}
        </StyledIconContainer>
      )}
    </StyledTextFieldWrapper>

    {(errorCaption || helpCaption) && !hideCaption && (
      <Caption
        id={`describe-${name}`}
        role="alert"
        isError={Boolean(errorCaption)}
        message={errorCaption || helpCaption}
      />
    )}
  </StyledInputContainer>
);
